import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from './PrivateRoute';
import PermissionGuard from './PermissionGuard';
import RoleGuard from './RoleGuard';
import { usePermissions } from '../../hooks/usePermissions';
import type { Action, Module, Role } from '../../types/permissions';

interface ProtectedModuleProps {
  module: Module;
  action?: Action;
  children: ReactNode;
}

interface ProtectedRoleProps {
  roles: Role[];
  children: ReactNode;
}

interface ProtectedSiteAdminProps {
  children: ReactNode;
}

/**
 * Combina autenticación (ProtectedRoute) + permiso por módulo (PermissionGuard).
 * Úsalo como `element` de una <Route>.
 *
 *   <Route path="/gastos" element={
 *     <ProtectedModule module="financial"><GastosPage/></ProtectedModule>
 *   } />
 */
export const ProtectedModule = ({ module, action, children }: ProtectedModuleProps) => (
  <ProtectedRoute>
    <PermissionGuard module={module} action={action}>
      {children}
    </PermissionGuard>
  </ProtectedRoute>
);

/**
 * Combina autenticación + verificación de rol de EMPRESA.
 *
 *   <Route path="/admin/usuarios" element={
 *     <ProtectedRole roles={['owner','admin']}><UsersManagementPage/></ProtectedRole>
 *   } />
 */
export const ProtectedRole = ({ roles, children }: ProtectedRoleProps) => (
  <ProtectedRoute>
    <RoleGuard roles={roles}>{children}</RoleGuard>
  </ProtectedRoute>
);

/**
 * Combina autenticación + verificación de admin del SITIO (profiles.role === 'admin').
 * Para cosas globales como devtools y hoja de ruta (no por empresa).
 *
 *   <Route path="/devtools" element={
 *     <ProtectedSiteAdmin><DevToolsPage/></ProtectedSiteAdmin>
 *   } />
 */
export const ProtectedSiteAdmin = ({ children }: ProtectedSiteAdminProps) => (
  <ProtectedRoute>
    <SiteAdminGate>{children}</SiteAdminGate>
  </ProtectedRoute>
);

const SiteAdminGate = ({ children }: { children: ReactNode }) => {
  const { isSiteAdmin, permissionsLoading } = usePermissions();
  // El rol de sitio llega junto con el profile: mientras carga (ej: recarga
  // completa en /devtools, o al volver de una impersonación) no redirigir.
  if (permissionsLoading) return null;
  if (!isSiteAdmin) return <Navigate to="/acceso-denegado" replace />;
  return <>{children}</>;
};

